import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useState, useSyncExternalStore } from 'react'
import { isAndroidORIosApp, isIosApp } from 'webtonative'
import { Dialogs, useDialogs } from '../Contexts/DialogsContext'
import { useGlobalContext } from '../Contexts/GlobalContext'
import SignUp from '../Dialogs/Forms/SignUp'
import { getAppConfig, IAppConfig } from '../globalHeader/services/AppConfigService'
import AppleAuth from './AppleAuth'
import FacebookAuth from './FacebookAuth'
import GoogleAuth from './GoogleAuth'
import NativeSocialLogin from './NativeSocialLogin'

export enum SocialType {
    FACEBOOK = 1,
    GOOGLE = 2,
    APPLE = 3,
}

export type Theme = 'light' | 'dark'

export type NativeSsoProviders = Record<'apple' | 'google' | 'facebook', boolean>

// Stamped by a W2N JS Override in each app build that ships the SocialLogin
// plugins; older store builds never set it. Android runs overrides on page-load
// finished, after the first render, hence the subscription (play-area ADR-018).
declare global {
    interface Window {
        __ICC_NATIVE_FEATURES__?: { sso?: Partial<NativeSsoProviders> }
    }
}

const NATIVE_FEATURES_EVENT = 'icc-native-features'

const subscribeNativeFeatures = (onChange: () => void) => {
    window.addEventListener(NATIVE_FEATURES_EVENT, onChange)
    return () => window.removeEventListener(NATIVE_FEATURES_EVENT, onChange)
}

interface IProps {
    title: string
    titleFontStyles?: any
}

const SocialLogin: React.FC<IProps> = (props) => {
    const { title } = props
    const {
        onLoginSuccessful,
        isGuest,
        googleClientId,
        facebookAppId,
        facebookApiVersion,
        appleClientId,
        appleRedirectURL,
        setSocialData,
    } = useGlobalContext()
    // Synchronous lazy init (cookie/localStorage) — reading this in a
    // useEffect gave the first render a null config. Only consulted for
    // the web path's hideSSO; the native/web split keys on the wrapper flag below.
    const [appConfig] = useState<IAppConfig | null>(getAppConfig)
    const nativeSso = useSyncExternalStore(
        subscribeNativeFeatures,
        () => window.__ICC_NATIVE_FEATURES__?.sso,
        () => undefined,
    )
    const { registerDialog, unregisterDialog } = useDialogs()

    const onSignUpSocial = (socialResponse: any) => {
        setSocialData(socialResponse)
        unregisterDialog(Dialogs.SIGNIN)
        unregisterDialog(Dialogs.SIGNUP)
        setTimeout(() => {
            registerDialog(Dialogs.SIGN_UP_SOCIAL, <SignUp isSocialSignup />)
        }, 100)
    }

    if (!(googleClientId || facebookAppId || facebookApiVersion || appleClientId || appleRedirectURL)) {
        return null
    }

    // The wrapper decides the SSO path: webtonative stamps this flag before any
    // JS runs, so the first render already routes native — the web SDK scripts
    // (doomed inside a WebView, PLAY-AREA-2EX) are never requested there.
    if (isAndroidORIosApp) {
        // Only providers the build declares — an undeclared one is a dead button.
        const nativeProviders: NativeSsoProviders = {
            apple: isIosApp && nativeSso?.apple === true,
            google: nativeSso?.google === true,
            facebook: nativeSso?.facebook === true,
        }
        if (!nativeProviders.apple && !nativeProviders.google && !nativeProviders.facebook) {
            return null
        }
        return (
            <Stack gap={2} sx={{ width: '100%' }}>
                <Typography textAlign="center" variant="body2">
                    {title}
                </Typography>
                <NativeSocialLogin onSignUpSocial={onSignUpSocial} providers={nativeProviders} />
            </Stack>
        )
    }

    // Web-SDK SSO only: the mobileApp deep link sets it so website pages opened
    // inside the wrapper (no build check there) stay hidden.
    if (appConfig && appConfig.hideSSO) {
        return null
    }

    return (
        <Stack gap={2} sx={{ width: '100%' }}>
            <Typography textAlign="center" variant="body2">
                {title}
            </Typography>
            <Stack
                gap={4}
                direction="row"
                justifyContent="space-around"
                sx={{
                    height: '70px',
                }}
            >
                {googleClientId && (
                    // <GoogleOAuthProvider clientId={googleClientId}>
                    //     <GoogleAuthV2
                    //         onSignUpSocial={onSignUpSocial}
                    //         onLoginSuccessful={onLoginSuccessful}
                    //         isGuest={isGuest}
                    //         googleClientId={googleClientId}
                    //     />
                    // </GoogleOAuthProvider>
                    <GoogleAuth
                        onSignUpSocial={onSignUpSocial}
                        onLoginSuccessful={onLoginSuccessful}
                        isGuest={isGuest}
                        googleClientId={googleClientId}
                    />
                )}
                {appleClientId && appleRedirectURL && (
                    <AppleAuth
                        onSignUpSocial={onSignUpSocial}
                        onLoginSuccessful={onLoginSuccessful}
                        isGuest={isGuest}
                        appleClientId={appleClientId}
                        appleRedirectURL={appleRedirectURL}
                    />
                )}
                {facebookAppId && facebookApiVersion && (
                    <FacebookAuth
                        onSignUpSocial={onSignUpSocial}
                        onLoginSuccessful={onLoginSuccessful}
                        isGuest={isGuest}
                        facebookAppId={facebookAppId}
                        facebookApiVersion={facebookApiVersion}
                    />
                )}
            </Stack>
        </Stack>
    )
}

SocialLogin.displayName = 'SocialLogin'

export default SocialLogin
